import { useRef, useState } from "react"

import {
  SIGNUP_STATES,
  buildSignupPayload,
  createSignupStartedAt,
  formatCooldownMessage,
  getSignupCooldownRemainingMs,
  getSignupErrorMessage,
  setSignupCooldown,
  shouldRefreshStartedAt,
  submitSignupRequest,
} from "../utils/signup"

export const useSignupSubmission = source => {
  const startedAtRef = useRef(createSignupStartedAt())
  const [submissionState, setSubmissionState] = useState(SIGNUP_STATES.idle)
  const [submitError, setSubmitError] = useState("")

  const logSignupResult = (status, details) => {
    console.log("[signup-flow]", {
      source,
      status,
      ...details,
    })
  }

  const refreshStartedAt = () => {
    startedAtRef.current = createSignupStartedAt()
  }

  const resetFeedback = () => {
    if (
      submissionState === SIGNUP_STATES.error ||
      submissionState === SIGNUP_STATES.cooldown
    ) {
      setSubmissionState(SIGNUP_STATES.idle)
      setSubmitError("")
    }

    if (shouldRefreshStartedAt(startedAtRef.current)) {
      refreshStartedAt()
    }
  }

  const submitSignup = async values => {
    const cooldownRemaining = getSignupCooldownRemainingMs()

    if (cooldownRemaining > 0) {
      setSubmissionState(SIGNUP_STATES.cooldown)
      const message = formatCooldownMessage(cooldownRemaining)
      setSubmitError(message)
      logSignupResult("blocked", {
        reason: "cooldown",
        message,
      })
      return false
    }

    setSubmissionState(SIGNUP_STATES.submitting)
    setSubmitError("")

    try {
      await submitSignupRequest(
        buildSignupPayload({
          email: values.email,
          source,
          website: values.website,
          startedAt: startedAtRef.current,
        })
      )

      setSignupCooldown()
      setSubmissionState(SIGNUP_STATES.success)
      logSignupResult("success", {
        email: values.email,
      })
      return true
    } catch (error) {
      const message = getSignupErrorMessage(error)

      setSubmissionState(
        error.statusCode === 429 ? SIGNUP_STATES.cooldown : SIGNUP_STATES.error
      )
      setSubmitError(message)
      logSignupResult("failed", {
        email: values.email,
        reason: error.reason || "unknown_error",
        statusCode: error.statusCode || 500,
        message,
      })

      if (error.reason === "stale_submission") {
        refreshStartedAt()
      }

      return false
    }
  }

  return {
    submissionState,
    submitError,
    submitSignup,
    resetFeedback,
  }
}
